import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Button from "../Button";

import CertificateActions, { useCertificateActions } from "../../context/CertificateActions";
import type { CourseCertificate } from "../../api/compliance";

import CertificateStatus from "./CertificateStatus";
import Actions from "./Actions";
import ValidityPeriod from "./ValidityPeriod";
import CourseStatusIcon from "../CourseStatusIcon";
import CertificateList from "./CertificateList";

interface RefresherProps {
  courseCertificate: CourseCertificate;
}

const borderlessCellStyle: React.CSSProperties = { border: 0 };
const iconCellStyle: React.CSSProperties = { width: "1rem" };
const validityPeriodCellStyle: React.CSSProperties = { whiteSpace: "nowrap", width: "15%" };
const validUntilCellStyle: React.CSSProperties = { width: "15%" };
const actionsCellStyle: React.CSSProperties = { width: "12%" };
const iconContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  whiteSpace: "nowrap",
};
const expandedCellStyle: React.CSSProperties = {
  backgroundColor: "#f9f9f9",
  overflow: "hidden",
  padding: "0.5rem 0",
};

const Refresher: FC<RefresherProps> = ({ courseCertificate }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const refreshers: CourseCertificate[] = [
    ...(courseCertificate.refreshers?.filter((r) => !r.inactive) || [])
      .map((r) => {
        return {
          ...r,
          refreshers: undefined,
        };
      })
      .sort((a, b) => {
        return b.id - a.id;
      }),
  ];
  const rowStyle = visible ? borderlessCellStyle : undefined;
  const getCellStyle = (cellStyle: React.CSSProperties = {}) =>
    visible ? { ...cellStyle, ...borderlessCellStyle } : cellStyle;
  const actions = useCertificateActions();
  const certificate = courseCertificate.actions?.downloadableCertificate || courseCertificate.certificate;
  if (!certificate) {
    return null;
  }
  const parentCoversRefreshers = certificate.status === "ok" || certificate.status === "never_expires";
  const name = courseCertificate.name?.replace(/ \W initial$/i, "") || "";
  const coveringCourseName = refreshers.find((refresher) => refresher.id === certificate.id)?.name ?? name;

  return (
    <>
      <TableRow style={rowStyle}>
        <TableCell style={getCellStyle(iconCellStyle)}>
          <div style={iconContainerStyle}>
            <CourseStatusIcon status={certificate.status} />
          </div>
        </TableCell>
        <TableCell onClick={() => setVisible(!visible)} style={getCellStyle({ cursor: "pointer" })}>
          ({courseCertificate.id}) {name}
          <Button
            title={visible ? t("certificate.close") : t("certificate.open")}
            icon={visible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          />
        </TableCell>
        <TableCell style={getCellStyle(validityPeriodCellStyle)}>
          <ValidityPeriod courseCertificate={courseCertificate} />
        </TableCell>
        <TableCell style={getCellStyle(validUntilCellStyle)}>
          <CertificateStatus validUntil={certificate.validUntil} status={certificate.status} />
        </TableCell>
        <TableCell style={getCellStyle(actionsCellStyle)} align={"justify"}>
          <Actions courseCertificate={courseCertificate} />
        </TableCell>
      </TableRow>
      {visible && (
        <TableRow>
          <CertificateActions.Provider
            value={{
              getBoughtCourses() {
                return actions.getBoughtCourses();
              },
              showHistory(certificate: any) {
                return actions.showHistory(certificate);
              },
            }}
          >
            <TableCell colSpan={5} style={expandedCellStyle}>
              <CertificateList
                certificates={refreshers}
                coveredByParent={parentCoversRefreshers}
                coveredByParentCourseName={coveringCourseName}
                header={false}
                variant="borderless"
              />
            </TableCell>
          </CertificateActions.Provider>
        </TableRow>
      )}
    </>
  );
};

export default Refresher;
